import { beforeSendSpan } from "@lib/otel/scrub";
import { beforeSendTransaction } from "@lib/otel/transaction";
import { tracesSampler } from "@lib/otel/sampling";

export type ConvoComponent = "web-client" | "web-server" | "worker";

export function resolveSentryEnvironment(): string {
  return process.env.SENTRY_ENVIRONMENT?.trim() || process.env.NODE_ENV?.trim() || "development";
}

export function resolveSentryRelease(): string | undefined {
  const release =
    process.env.SENTRY_RELEASE?.trim() || process.env.NEXT_PUBLIC_SENTRY_RELEASE?.trim();
  return release || undefined;
}

/**
 * Local-only, never for production. `SENTRY_SPOTLIGHT=1` (or `true`) points
 * the SDK at its default sidecar address, `http://localhost:8969/stream`;
 * a full URL is passed through as-is, which matters for this codebase because
 * the app runs inside a dev container - the sidecar it needs to reach is not
 * necessarily at ITS localhost. `0`/`false` and unset both resolve to
 * `undefined` so the key stays out of the init options entirely rather than
 * carrying a falsy value; `config.test.ts` and `instrument.test.ts` both pin
 * that absence, since this is exactly the kind of flag that leaks into a
 * production image if it defaults to being present.
 */
export function resolveSentrySpotlight(): boolean | string | undefined {
  const value = process.env.SENTRY_SPOTLIGHT?.trim();
  if (!value) {
    return undefined;
  }
  const normalized = value.toLowerCase();
  if (normalized === "1" || normalized === "true") {
    return true;
  }
  if (normalized === "0" || normalized === "false") {
    return undefined;
  }
  return value;
}

export function resolveConvoComponent(fallback: ConvoComponent = "web-server"): ConvoComponent {
  const value = process.env.CONVO_COMPONENT?.trim();
  if (value === "web-client" || value === "web-server" || value === "worker") {
    return value;
  }
  return fallback;
}

/**
 * Sentry integrations we remove from its defaults.
 *
 * Every one of these is added by `getAutoPerformanceIntegrations()`, which the
 * SDK only installs once `hasSpansEnabled()` is true - and `tracesSampler`
 * below is what made it true for the first time in this codebase. So these
 * three arrived as a side effect of turning tracing on, not by anyone asking
 * for them.
 *
 * `Prisma`: it performs the Prisma 7 handshake (setting
 * `V7_PRISMA_INSTRUMENTATION` on `globalThis`, which
 * `@prisma/client/runtime/client.js` reads back), and the spans that produces
 * carry `db.query.text`, `db.statement` and `db.connection_string`. Verified by
 * running it: the connection string, password included, lands on a span. That
 * is the exact leak `lib/prisma.ts`'s hand-built extension exists to avoid, and
 * it also duplicates every query span.
 *
 * `Postgres` / `PostgresJs`: the DRIVER-level pair, separate from `Prisma` and
 * a level below it - `Postgres` wraps the `pg` driver, `PostgresJs` wraps
 * `postgres.js`. Filtering `Prisma` did not touch them, and they are the reason
 * this entry exists: both NAME their `db` span after the raw SQL statement, so
 * a full `SELECT "public"."User"."id", ... WHERE ... LIKE ($1)` ships as
 * `span.description`. `beforeSendSpan` scrubs `span.data` and cannot reach a
 * span name, so no allowlist entry and no denied key stops that - only not
 * creating the span does. Today the leak is schema, table and column names,
 * because every raw query in this codebase is a parameterized tagged template
 * and values arrive as `$1`. That is a convention, not a mechanism: one
 * `$queryRawUnsafe` with an interpolated value would put learner text in a span
 * name, and nothing downstream would catch it.
 *
 * Nothing is lost by removing them. `lib/prisma.ts`'s own client extension
 * emits `prisma <model>.<operation>` spans with `db.*` attributes, so database
 * visibility comes from the seam we control; Sentry's driver spans were
 * duplicating it underneath.
 *
 * `OpenAI` / `Anthropic_AI`: we own this seam deliberately - `withGenAiSpan` in
 * `lib/ai/index.ts`, with its streaming-lifetime handling and cost attribution.
 * Two competing gen_ai spans per call, disagreeing about `gen_ai.request.model`,
 * is worse than either alone. They also write `gen_ai.response.text`.
 *
 * The scrubber denies all of those keys too. This is belt and braces on
 * purpose: name-based filtering breaks silently if Sentry renames an
 * integration, and the denylist alone would still leave the duplicate spans.
 * For the two Postgres entries the filter is not belt and braces but the ONLY
 * defence, since the payload is the span name.
 */
export const DISABLED_SENTRY_INTEGRATIONS: readonly string[] = [
  "Prisma",
  "Postgres",
  "PostgresJs",
  "OpenAI",
  "Anthropic_AI",
];

export function buildSentryInitOptions(
  component: ConvoComponent,
  dsn?: string,
  spotlight?: boolean | string,
) {
  return {
    ...(dsn ? { dsn } : {}),
    environment: resolveSentryEnvironment(),
    release: resolveSentryRelease(),
    initialScope: {
      tags: { component },
    },
    /**
     * Never on, and stated rather than defaulted. `sendDefaultPii` would attach
     * request headers, cookies and IP addresses to events, all of which are
     * learner-identifying. D-4 forbids it and `config.test.ts` pins it.
     *
     * DO NOT add a `dataCollection` key next to this, not even `{}`. In
     * `@sentry/core` 10.69 `resolveDataCollectionOptions` branches on
     * `options.dataCollection != null`, so ANY object there - empty included -
     * makes the SDK ignore `sendDefaultPii` entirely and apply its all-on
     * defaults: `genAI {inputs, outputs}`, cookies, request and response
     * headers, query params, database query data and all four http bodies.
     * An empty object read as "no opinion" is read by the SDK as "collect
     * everything". `config.test.ts` asserts the key is absent.
     */
    sendDefaultPii: false,
    /**
     * See `DISABLED_SENTRY_INTEGRATIONS`. Typed generically rather than against
     * `Integration` so this stays importable from both the browser and the
     * server entrypoint without pulling a server type into the client bundle.
     */
    integrations: <T extends { name: string }>(defaults: T[]): T[] =>
      defaults.filter((integration) => !DISABLED_SENTRY_INTEGRATIONS.includes(integration.name)),
    /**
     * Head-based. See `lib/otel/sampling.ts` for what that can and cannot do.
     */
    tracesSampler,
    /**
     * The last line of defence before a span leaves the process. See
     * `lib/otel/scrub.ts`; it fails closed.
     */
    beforeSendSpan,
    /**
     * Names the transaction from its route template and drops the duplicate
     * server spans underneath it. See `lib/otel/transaction.ts` for why this
     * is done here rather than by configuring the http integration.
     */
    beforeSendTransaction,
    /**
     * Local-only sidecar (see `resolveSentrySpotlight`). Present only when
     * requested - never a bare `false` or `undefined` - so it cannot silently
     * leak into a production init. No DSN is required alongside it: verified
     * empirically that `Sentry.init` with `dsn` omitted and `spotlight` set
     * still starts a tracer and delivers spans to the sidecar.
     */
    ...(spotlight ? { spotlight } : {}),
  };
}
